<template>
  <div v-if="shouldDrawLayer">
    <mgl-raster-layer v-if="isRasterLayer" />
    <div v-else>
      <div v-for="layer in mapboxComponentLayers" :key="layer.feature.mapboxLayerId">
        <!-- feature -->
        <mgl-vector-layer
          ref="vectorFeatureLayer"
          :source="layer.feature.source"
          :source-id="layer.feature.sourceId"
          :layer-id="layer.feature.mapboxLayerId"
          :layer="layer.feature.mapboxLayer"
          :before="AFTER_FEATURES_LAYER"
          :clear-source="false"
        />
        <!-- highlight -->
        <mgl-vector-layer
          v-if="layer.highlight"
          ref="vectorHighlightLayer"
          :source-id="layer.highlight.sourceId"
          :layer-id="layer.highlight.mapboxLayerId"
          :layer="layer.highlight.mapboxLayer"
          :before="AFTER_HIGHLIGHTS_LAYER"
          :clear-source="false"
        />
        <!-- label -->
        <mgl-vector-layer
          v-if="layer.label"
          ref="vectorLabelLayer"
          :source="layer.label.source"
          :source-id="layer.label.sourceId"
          :layer-id="layer.label.mapboxLayerId"
          :layer="layer.label.mapboxLayer"
          :before="AFTER_LABELS_LAYER"
          :clear-source="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { MglVectorLayer, MglRasterLayer } from 'v-mapbox';

import {
  DEFAULT_MIN_ZOOM_TO_VIEW_LABELS,
  HIGHLIGHT_SOURCE,
  AFTER_FEATURES_LAYER,
  AFTER_HIGHLIGHTS_LAYER,
  AFTER_LABELS_LAYER,
} from '../constants';
import { isRasterLayer } from '../guards';
import {
  getVectorOrGeoJsonLayerConfig,
  shouldDrawLayer,
  getSourceUrl,
  getHighlightMapboxLayerProperties,
  getHighlightMapboxLayerId,
  getCircleMapboxLayerId,
  vetroLayerIdToMapboxLayerId,
  labelLayerSourceIdToCentroidSourceId,
  vetroLayerToInternalVectorSourceName,
} from '../util/layers';
import {
  toPolygonOutline,
  isPolygonOutlineMapboxLayer,
  toDashedLine,
  toSolidLine,
  isDashedLineLayer,
  isCircleLayer,
  toHighlightLayer,
  toCircleLayer,
  toPolygonHighlightFill,
} from '../util/layers/types';
import { isPolygon, isLineString } from '../util/geom';
import { getPolygonOutlineMapboxLayerId, getDashedLineMapboxLayerId } from '../util/layers/names';
import { getDasharrayFilter } from '../util/layers/categorizedStyleFilter';

export default {
  name: 'vetro-feature-layer',
  components: {
    MglVectorLayer,
    MglRasterLayer,
  },
  inject: ['map'],
  // Provided for callers who need to refresh the tiles for this layer
  expose: ['refreshTileCache', 'moveBeforeLayer'],
  props: {
    // Vetro layer object as returned from our API, not a Mapbox GL layer object
    vetroLayer: { type: Object, required: true },
    // TODO remove tileServer prop (and make tileServers required) once FE is fully n'sync
    tileServer: { type: String, default: '' },
    tileServers: { type: Array, default: () => [] },
    // True to render labels in Imperial units, false to render in Metric
    useImperialUnits: { type: Boolean, default: true },
    // True for the layer to be visible, false to hide
    visible: { type: Boolean, default: true },
    // Mapbox GL filter expression to apply to this layer (and its labels)
    filter: { type: Array, required: false },
    labelMinZoom: { type: Number, default: DEFAULT_MIN_ZOOM_TO_VIEW_LABELS },
    labelAttributes: { type: Array },
  },
  data() {
    return {
      AFTER_FEATURES_LAYER,
      AFTER_HIGHLIGHTS_LAYER,
      AFTER_LABELS_LAYER,
      editableExtent: DEFAULT_MIN_ZOOM_TO_VIEW_LABELS,
    };
  },

  computed: {
    mapboxComponentLayers() {
      return this.layerToMapboxComponentLayers(this.vetroLayer);
    },
    isRasterLayer() {
      return isRasterLayer(this.vetroLayer);
    },
    shouldDrawLayer() {
      return shouldDrawLayer(this.vetroLayer);
    },
    computedFilter() {
      // If we don't have a filter, we want to return a dummy filter
      //  so that vue-mapbox always sets up a watcher on it
      if (this.filter?.length > 0) {
        return this.filter;
      }
      return ['all'];
    },
  },
  methods: {
    layerToMapboxComponentLayers(layer) {
      if (isLineString(layer)) {
        return [
          this.createMapboxConfig(toDashedLine(layer)),
          this.createMapboxConfig(toSolidLine(layer)),
        ];
      }

      if (isPolygon(layer)) {
        return [this.createMapboxConfig(toPolygonOutline(layer)), this.createMapboxConfig(layer)];
      }

      return [this.createMapboxConfig(toCircleLayer(layer)), this.createMapboxConfig(layer)];
    },
    getSourceUrls(options = {}) {
      if (this.tileServers.length > 0) {
        return this.tileServers.map((tileServer) =>
          getSourceUrl({ tileServer, vetroLayer: this.vetroLayer, ...options }),
        );
      }

      if (this.tileServer.length > 0) {
        return [
          getSourceUrl({ tileServer: this.tileServer, vetroLayer: this.vetroLayer, ...options }),
        ];
      }

      throw new Error('No tile server(s) provided');
    },
    moveBeforeLayer(afterLayer) {
      if (afterLayer) {
        const afterLayerMapboxComponentLayers = this.layerToMapboxComponentLayers(afterLayer);
        this.mapboxComponentLayers.forEach(
          ({ feature: featureLayer, highlight: highlightLayer, label: labelLayer }) => {
            if (featureLayer) {
              afterLayerMapboxComponentLayers.forEach(({ feature: afterFeatureLayer }) => {
                if (afterFeatureLayer) {
                  this.map.moveLayer(afterFeatureLayer.mapboxLayerId, featureLayer.mapboxLayerId);
                }
              });
            }
            if (highlightLayer) {
              afterLayerMapboxComponentLayers.forEach(({ highlight: afterHighlightLayer }) => {
                if (afterHighlightLayer) {
                  this.map.moveLayer(
                    afterHighlightLayer.mapboxLayerId,
                    highlightLayer.mapboxLayerId,
                  );
                } else {
                  this.map.moveLayer(featureLayer.mapboxLayerId, highlightLayer.mapboxLayerId);
                }
              });
            }
            if (labelLayer) {
              afterLayerMapboxComponentLayers.forEach(({ label: afterLabelLayer }) => {
                if (afterLabelLayer) {
                  this.map.moveLayer(afterLabelLayer.mapboxLayerId, labelLayer.mapboxLayerId);
                } else if (highlightLayer) {
                  this.map.moveLayer(highlightLayer.mapboxLayerId, labelLayer.mapboxLayerId);
                } else {
                  this.map.moveLayer(featureLayer.mapboxLayerId, labelLayer.mapboxLayerId);
                }
              });
            }
          },
        );
      }
    },
    refreshTileCache() {
      const sourceName = vetroLayerToInternalVectorSourceName(this.vetroLayer);
      const source = this.map.getSource(sourceName);

      if (!source) return;

      source.setTiles(this.getSourceUrls());
    },
    getHighlightMapboxLayer(layer) {
      const properties = getHighlightMapboxLayerProperties(layer);

      const mapboxFormattedProperties = this.map.U.properties(properties);

      return mapboxFormattedProperties;
    },
    getMapboxLayerId(layer) {
      const { id, geomType } = layer;
      return vetroLayerIdToMapboxLayerId(id, {
        [id]: geomType,
      });
    },
    getFeatureLayerMapboxConfig({ layer, type, style, sourceName }) {
      const dasharrayFilter = getDasharrayFilter(layer, this.activeStyleLayerId);

      // We use mapbox-gl-util's `properties()` to convert from mapbox-gl-utils's
      //  convenient format to Mapbox GL format so our vue-mapbox components can use them
      const featureMapboxLayer = {
        type,
        filter: dasharrayFilter ? [...this.computedFilter, dasharrayFilter] : this.computedFilter,
        ...this.map.U.properties(style),
      };

      let featureMapboxLayerId = this.getMapboxLayerId(layer);

      if (isPolygonOutlineMapboxLayer(layer)) {
        featureMapboxLayerId = getPolygonOutlineMapboxLayerId(featureMapboxLayerId);
      } else if (isDashedLineLayer(layer)) {
        featureMapboxLayerId = getDashedLineMapboxLayerId(featureMapboxLayerId);
      } else if (isCircleLayer(layer)) {
        featureMapboxLayerId = getCircleMapboxLayerId(featureMapboxLayerId);
      }

      const sourceUrls = this.getSourceUrls({ simplifyGeom: layer.style.simplifyGeom });

      return {
        mapboxLayerId: featureMapboxLayerId,
        mapboxLayer: featureMapboxLayer,
        sourceId: sourceName,
        source: { tiles: sourceUrls },
      };
    },
    getHighlightLayerMapboxConfig({ layer }) {
      // Dashed lines have a corresponding solid line layer used for
      // highlighting
      if (isDashedLineLayer(layer)) return null;

      const highlightLayer = toHighlightLayer(layer);

      const highlightMapboxLayerId = getHighlightMapboxLayerId(
        this.getMapboxLayerId(highlightLayer),
        !isPolygonOutlineMapboxLayer(layer),
        isCircleLayer(layer),
      );

      const highlightMapboxLayer = this.getHighlightMapboxLayer(highlightLayer);

      return {
        mapboxLayerId: highlightMapboxLayerId,
        mapboxLayer: highlightMapboxLayer,
        sourceId: HIGHLIGHT_SOURCE,
      };
    },
    getLabelLayerMapboxConfig(layer, { labelStyle, labelMapboxLayerId, sourceName }) {
      // If the layer is a dashed line or polygon outline, the label will be applied
      // only for its solid or non-outline counterpart.
      const noLabel = isDashedLineLayer(layer) || isPolygonOutlineMapboxLayer(layer) || !labelStyle;

      if (noLabel) return null;

      const labelMapboxLayer = {
        type: 'symbol',
        filter: this.computedFilter,
        ...this.map.U.properties(labelStyle),
      };

      const useCentroid = isPolygon(layer);

      const sourceUrls = this.getSourceUrls({ useCentroid, includeRequiredAttributes: false });

      const sourceId = useCentroid ? labelLayerSourceIdToCentroidSourceId(sourceName) : sourceName;

      return {
        mapboxLayerId: labelMapboxLayerId,
        mapboxLayer: labelMapboxLayer,
        sourceId,
        source: { tiles: sourceUrls },
      };
    },
    createMapboxConfig(layer) {
      if (this.isRasterLayer) {
        throw new Error('Raster layers not yet implemented');
      }

      const visibility = this.visible ? 'visible' : 'none';
      const { type, sourceName, style, labelMapboxLayerId, labelStyle } =
        getVectorOrGeoJsonLayerConfig({
          layer,
          mapboxLayerId: this.getMapboxLayerId(layer),
          useImperialUnits: this.useImperialUnits,
          visibility,
          labelMinZoom: this.labelMinZoom,
        });

      // Add the polygon highlight fill style ONLY IF the feature is a non-outline (multi) polygon;
      // in other words, DO NOT add the fill style IF the feature is an outline polygon, point, or line.
      const useFill = layer.geomType.includes('Polygon') && !isPolygonOutlineMapboxLayer(layer);
      const highlightLayer = useFill ? toPolygonHighlightFill(layer) : layer;

      // For each vetro layer, we need up to three mapbox layers - the features themselves,
      // their labels (if applicable), and their highlight layer.
      return {
        feature: this.getFeatureLayerMapboxConfig({ layer, type, style, sourceName }),
        highlight: this.getHighlightLayerMapboxConfig({ layer: highlightLayer }),
        label: this.getLabelLayerMapboxConfig(layer, {
          labelStyle,
          labelMapboxLayerId,
          sourceName,
        }),
      };
    },
  },
};
</script>
